import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AHIcon, AHSeparator, Box, ListItem, TodoPrioIcon } from "arkh";
import React from "react";
import { WorkspaceTodoListItemDescription } from "../../WorkspaceTodoListItemDescription";
import { graphql, useFragment, useMutation } from "react-relay";
import { WorkspaceTodoEditMainSubTodos_todoWorkspace$key } from "./__generated__/WorkspaceTodoEditMainSubTodos_todoWorkspace.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { CSS } from "@dnd-kit/utilities";
import { WorkspaceTodoEditMainSubTodosSaveNewOrderMutation } from "./__generated__/WorkspaceTodoEditMainSubTodosSaveNewOrderMutation.graphql";
export function WorkspaceTodoEditMainSubTodos(props: {
  todo: WorkspaceTodoEditMainSubTodos_todoWorkspace$key;
  disabled: boolean;
  onCreateNewSubTodo: () => void;
  onOpenTodo: (id: string) => void;
}) {
  const todo = useFragment(
    graphql`
      fragment WorkspaceTodoEditMainSubTodos_todoWorkspace on TodoWorkspace {
        id
        subWorkspaceTodos {
          id
          title
          ...WorkspaceTodoListItemDescription_todo
          priority
        }
      }
    `,
    props.todo
  );

  const [saveNewOrder] =
    useMutation<WorkspaceTodoEditMainSubTodosSaveNewOrderMutation>(graphql`
      mutation WorkspaceTodoEditMainSubTodosSaveNewOrderMutation(
        $id: ID!
        $newOrder: [String!]!
      ) @raw_response_type {
        todoWorkspace(id: $id) {
          updateSubTodoOrder(newSubTodoOrder: $newOrder) {
            id
            subWorkspaceTodos {
              id
              title
              priority
            }
          }
        }
      }
    `);
  const intl_tokens = useIntlTokens();
  const subWorkspaceTodos = todo.subWorkspaceTodos?.slice() ?? [];
  const sensors = useSensors(useSensor(PointerSensor));
  return (
    <Box spaceTop="medium">
      <ListItem
        color={props.disabled ? "transparent" : "surface-primary"}
        border="border-primary"
        left={<AHIcon type="workspace" color="darkText" />}
        right={<AHIcon type="add-circle" color="darkText" />}
        isFirst
        isLast={(todo.subWorkspaceTodos?.length ?? 0) === 0}
        onPress={props.onCreateNewSubTodo}
      >
        <ListItem.TitleSecondary>
          {intl_tokens.SubTodos}
        </ListItem.TitleSecondary>
      </ListItem>

      <DndContext
        sensors={sensors}
        modifiers={[
          restrictToVerticalAxis,
          restrictToFirstScrollableAncestor,
          restrictToParentElement,
        ]}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over) {
            return;
          }
          const oldIndex = subWorkspaceTodos.findIndex(
            (item) => item.id === active.id
          );
          const newIndex = subWorkspaceTodos.findIndex(
            (item) => item.id === over?.id
          );
          const newSubTodo = arrayMove(subWorkspaceTodos, oldIndex, newIndex);

          saveNewOrder({
            variables: {
              id: todo.id,
              newOrder: newSubTodo.map((item) => item.id),
            },
            optimisticResponse: {
              todoWorkspace: {
                updateSubTodoOrder: {
                  id: todo.id,
                  subWorkspaceTodos: newSubTodo.map((item) => ({
                    ...item,
                  })),
                },
              },
            },
          });
        }}
      >
        <SortableContext
          items={subWorkspaceTodos}
          strategy={verticalListSortingStrategy}
        >
          {subWorkspaceTodos.map((item, i, arr) => (
            <SortableItem id={item.id} key={item.id}>
              {(isLast, dragHandleProps) => (
                <>
                  <AHSeparator />
                  <ListItem
                    border="border-primary"
                    onPress={() => {
                      props.onOpenTodo(item.id);
                    }}
                    left={
                      <div {...dragHandleProps}>
                        <AHIcon
                          type="reorder"
                          color="washedText"
                          size="xsmall"
                        />
                      </div>
                    }
                    isLast={isLast}
                  >
                    <Box
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {item.priority === "DEFAULT" ? null : (
                        <TodoPrioIcon priority={item.priority} />
                      )}
                      <WorkspaceTodoListItemDescription workspaceTodo={item} />
                    </Box>
                  </ListItem>
                </>
              )}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}

function SortableItem(props: {
  id: string;
  children: (isLast: boolean, dragHandleProps: any) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    newIndex,
    items,
    isDragging,
  } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 0,
  };

  const isLast = newIndex === items.length - 1;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {props.children(isLast, listeners)}
    </div>
  );
}
